import { runInAction } from "mobx";
import type { ChangeEvent } from "react";
import type { BoxListItem, IWarehouseApi } from "../../../api/WarehouseApi";
import type { INavigationStore } from "../../../stores/navigation";
import { Screens } from "../../../stores/navigation";
import type { Product } from "../../../stores/Product";
import type { IStore } from "../../../stores/store";
import { Measures } from "../../../types/entities";
import { dateToInputValue, normalizeDate } from "../../../domain/product-form";
import {
  addProductPhoto,
  normalizeProductPhotoState,
} from "../../../domain/product-photos";
import {
  prepareImageFileDataUrl,
  validateImageFile,
} from "../../../utils/image-upload";
import { resolveEntityTypeTags } from "../../../utils/typeTags";
import {
  type EditProductFormState,
  applyUpdatedProduct,
  buildEditProductUpdatePayload,
  validateEditProductForm,
} from "./form-state";

export interface EditProductWorkflowState extends EditProductFormState {
  typeTagInput: string;
  availableBoxes: BoxListItem[];
  typeTagSuggestions: string[];
  isReferenceLoading: boolean;
  referenceError: string | null;
  isPhotoLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
}

function resolveErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function collectTypeTagSuggestions(
  products: Array<{ typeTags?: string[] }>,
): string[] {
  const tagSet = new Set<string>();

  products.forEach((item) => {
    resolveEntityTypeTags({
      typeTags: item.typeTags,
    }).forEach((tag) => tagSet.add(tag));
  });

  return Array.from(tagSet).sort();
}

export async function setPhotoFromFileOperation(input: {
  state: EditProductWorkflowState;
  event: ChangeEvent<HTMLInputElement>;
}): Promise<void> {
  const [file] = Array.from(input.event.target.files || []);
  input.event.target.value = "";

  if (!file) {
    return;
  }

  const validationError = validateImageFile(file);
  if (validationError) {
    input.state.error = validationError;
    return;
  }

  runInAction(() => {
    input.state.isPhotoLoading = true;
    input.state.error = null;
  });

  try {
    const dataUrl = await prepareImageFileDataUrl(file);
    runInAction(() => {
      const nextState = addProductPhoto(
        {
          photo: input.state.photo,
          photos: input.state.photos,
        },
        dataUrl,
        { makePrimary: true },
      );
      input.state.photo = nextState.photo;
      input.state.photos = nextState.photos;
    });
  } catch (error) {
    runInAction(() => {
      input.state.error = resolveErrorMessage(
        error,
        "Не удалось загрузить изображение",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isPhotoLoading = false;
    });
  }
}

export async function loadEditProductReferenceDataOperation(input: {
  state: EditProductWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<void> {
  runInAction(() => {
    input.state.isReferenceLoading = true;
    input.state.referenceError = null;
  });

  try {
    const [boxes, products] = await Promise.all([
      input.api.getAllBoxes(),
      input.api.getAllProducts(),
    ]);

    runInAction(() => {
      input.state.availableBoxes = boxes;
      input.state.typeTagSuggestions = collectTypeTagSuggestions(products);
      input.state.parentId = input.formatParentInputValue(input.state.parentId);
    });
  } catch (error) {
    runInAction(() => {
      input.state.referenceError = resolveErrorMessage(
        error,
        "Не удалось загрузить справочные данные",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isReferenceLoading = false;
    });
  }
}

export function resetEditProductFromProductOperation(input: {
  state: EditProductWorkflowState;
  product: Product;
  formatParentInputValue: (value: string) => string;
}): void {
  const photoState = normalizeProductPhotoState({
    photo: input.product.photo,
    photos: input.product.photos,
  });

  input.state.name = input.product.name || "";
  input.state.description = input.product.description || "";
  input.state.photo = photoState.photo;
  input.state.photos = photoState.photos;
  input.state.typeTags = resolveEntityTypeTags({
    typeTags: input.product.typeTags,
  });
  input.state.typeTagInput = "";
  input.state.parentId = input.formatParentInputValue(
    input.product.parentId || "",
  );
  input.state.measure = input.product.measure || Measures.quantity;
  input.state.quantityInput = String(input.product.quantity || 1);

  input.state.manufacturingDate = dateToInputValue(
    normalizeDate(input.product.manufacturingDate?.toJSON()),
  );
  input.state.expiryDate = dateToInputValue(
    normalizeDate(input.product.expiryDate?.toJSON()),
  );
  input.state.error = null;
  input.state.isPhotoLoading = false;
}

export async function submitEditProductOperation(input: {
  state: EditProductWorkflowState;
  api: IWarehouseApi;
  store: IStore;
  navigationStore: INavigationStore;
  product: Product;
  onUpdated?: (product: Product) => void | boolean | Promise<void | boolean>;
}): Promise<void> {
  const validationError = validateEditProductForm(
    input.state,
    input.state.availableBoxes,
  );
  if (validationError) {
    input.state.error = validationError;
    return;
  }

  runInAction(() => {
    input.state.isSubmitting = true;
    input.state.error = null;
  });

  try {
    const payload = buildEditProductUpdatePayload(
      input.state,
      input.state.availableBoxes,
    );
    const updated = await input.api.updateProduct(input.product.id, payload);

    runInAction(() => {
      applyUpdatedProduct(input.product, updated);
      const storeProduct = input.store.products.find(
        (item) => item.id === input.product.id,
      );
      if (storeProduct && storeProduct !== input.product) {
        applyUpdatedProduct(storeProduct, updated);
      }
    });

    const shouldContinue = input.onUpdated
      ? await input.onUpdated(input.product)
      : undefined;
    if (shouldContinue === false) {
      return;
    }
    input.navigationStore.setScreen(Screens.Product, input.product);
  } catch (error) {
    runInAction(() => {
      input.state.error = resolveErrorMessage(
        error,
        "Не удалось сохранить изменения продукта",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isSubmitting = false;
    });
  }
}
